import { v4 } from 'uuid';
import fieldTypes from '@admin/config/overrides/fieldTypes';
import { ErrorBoundary } from 'react-error-boundary';
import { TBlockFields, TBlockRender } from '@/lib/types/puck';
import {
  AppState,
  ComponentData,
  DefaultComponentProps,
  Fields,
  PuckComponent,
} from '@measured/puck';
import * as S from './styles.ts';
import React from 'react';

const handleFieldValues = (
  inputFields: TBlockFields
): {
  fields: TBlockFields;
  defaultValues: Record<string, any>;
} => {
  const fields: TBlockFields = {};
  const defaultValues: Record<string, any> = {};

  inputFields &&
    Object.keys(inputFields).forEach((key) => {
      const fieldValues = inputFields[key];

      switch (fieldValues.type) {
        case 'select':
        case 'radio':
          // set default value to first item in options, if no default value present
          fields[key] = {
            ...fieldValues,
            default:
              (fieldValues as any).default || fieldValues.options?.[0]?.value,
          };
          defaultValues[key] =
            (fieldValues as any).default || fieldValues.options?.[0]?.value;
          break;
        case 'boolean':
          fields[key] = {
            ...fieldValues,
            type: 'custom',
            render: fieldTypes.boolean,
          };
          defaultValues[key] = fieldValues.default;
          break;
        case 'image':
          fields[key] = {
            ...fieldValues,
            type: 'custom',
            render: fieldTypes.image,
          };
          defaultValues[key] = {
            src: '',
            alt: '',
          };
          break;
        case 'button':
          fields[key] = {
            ...fieldValues,
            type: 'custom',
            render: fieldTypes.button,
          };
          defaultValues[key] = {
            href: '',
            variant: 'solid',
            color: 'primary',
            title: 'Button',
          };
          break;
        case 'input':
          fields[key] = {
            ...fieldValues,
            type: 'custom',
            render: fieldTypes.input,
          };
          defaultValues[key] = {};
          break;
        case 'icon':
          fields[key] = {
            ...fieldValues,
            type: 'custom',
            render: fieldTypes.icon,
          };
          defaultValues[key] = 'Svg2Layers';
          break;
        case 'object':
          const objectFields = fieldValues.objectFields;
          const {
            fields: objectFieldValues,
            defaultValues: objectDefaultValues,
          } = handleFieldValues(objectFields);

          fields[key] = {
            ...fieldValues,
            objectFields: objectFieldValues,
            default: undefined,
          } as any;
          defaultValues[key] = objectDefaultValues;
          break;
        case 'array':
          const arrayFields = fieldValues.arrayFields as any;
          const defaultItemsCount = fieldValues.default_items || 0;
          const {
            fields: arrayFieldValues,
            defaultValues: arrayDefaultValues,
          } = handleFieldValues(arrayFields);

          fields[key] = {
            ...fieldValues,
            arrayFields: arrayFieldValues,
            default: undefined,
          } as any;
          defaultValues[key] = [...Array(defaultItemsCount)].map(() => ({
            key: v4(),
            ...arrayDefaultValues,
          }));
          break;
        case 'markdown':
          fields[key] = {
            ...fieldValues,
            type: 'custom',
            render: fieldTypes.markdown,
          };
          defaultValues[key] = [
            {
              type: 'paragraph',
              children: [{ text: '' }],
            },
          ];
          break;
        default:
          fields[key] = {
            ...fieldValues,
          };
          if ((fieldValues as any).default !== undefined) {
            defaultValues[key] = (fieldValues as any).default;
          }
          break;
      }
    });

  return {
    fields: { ...fields },
    defaultValues,
  };
};

type TResolveData = {
  props: {
    type: string;
    props: Record<string, any>;
  };
};

export type ComponentConfig<
  ComponentProps extends DefaultComponentProps = DefaultComponentProps,
  DefaultProps = ComponentProps,
  DataShape = Omit<ComponentData<ComponentProps>, 'type'>,
> = {
  render: PuckComponent<ComponentProps>;
  label?: string;
  defaultProps?: DefaultProps;
  fields?: Fields<ComponentProps>;
  permissions?: Partial<Permissions>;
  resolveFields?: (
    data: DataShape,
    params: {
      changed: Partial<Record<keyof ComponentProps, boolean>>;
      fields: Fields<ComponentProps>;
      lastFields: Fields<ComponentProps>;
      lastData: DataShape;
      appState: AppState;
    }
  ) => Promise<Fields<ComponentProps>> | Fields<ComponentProps>;
  resolveData?: (
    data: DataShape,
    params: {
      changed: Partial<Record<keyof ComponentProps, boolean>>;
      lastData: DataShape;
    }
  ) =>
    | Promise<{
        props?: Partial<ComponentProps>;
        readOnly?: Partial<Record<keyof ComponentProps, boolean>>;
      }>
    | {
        props?: Partial<ComponentProps>;
        readOnly?: Partial<Record<keyof ComponentProps, boolean>>;
      };
  resolvePermissions?: (
    data: DataShape,
    params: {
      changed: Partial<Record<keyof ComponentProps, boolean>>;
      lastPermissions: Partial<Permissions> | undefined;
      initialPermissions: Partial<Permissions>;
      appState: AppState;
    }
  ) => Partial<Permissions>;
};

type ICreateComponent<
  ComponentProps extends DefaultComponentProps = DefaultComponentProps,
  DefaultProps = ComponentProps,
  DataShape = Omit<ComponentData<ComponentProps>, 'type'>,
> = {
  component: TBlockRender;
  label: string;
} & {
  resolveFields?: (
    data: Omit<ComponentData<any>, 'type'>,
    params?: {
      changed: Partial<Record<keyof ComponentProps, boolean>>;
      fields: Fields<ComponentProps>;
      lastFields: Fields<ComponentProps>;
      lastData: DataShape;
      appState: AppState;
    }
  ) => Promise<TBlockFields> | TBlockFields;
  fields?: TBlockFields;
};

const Fallback = ({ error }: { error: Error }) => {
  return (
    <S.Fallback>
      <h1>SOMETHING WENT WRONG</h1>
      <S.Error>{error.message}</S.Error>
    </S.Fallback>
  );
};

const CustomErrorBoundary = ({ children }: { children: React.ReactNode }) => {
  return (
    <ErrorBoundary fallbackRender={Fallback as unknown as any}>
      {children}
    </ErrorBoundary>
  );
};

export const createComponent = ({
  component,
  label,
  ...props
}: ICreateComponent) => {
  switch (true) {
    case 'fields' in props:
      const { fields, defaultValues } = handleFieldValues(props.fields);

      return {
        ...props,
        label,
        fields,
        defaultProps: defaultValues,
        render: (props: any) => {
          return <CustomErrorBoundary>{component(props)}</CustomErrorBoundary>;
        },
      };
    case 'resolveFields' in props:
      const resolveFields = props.resolveFields;
      const modifiedResolveFields = async (props) => {
        const { fields } = handleFieldValues(await resolveFields(props));

        return fields;
      };

      return {
        ...props,
        label,
        resolveFields: modifiedResolveFields,
        defaultProps: {
          submit: {},
        },
        render: (props: any) => {
          return <CustomErrorBoundary>{component(props)}</CustomErrorBoundary>;
        },
      };
  }
};
